import { useMemo } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { AuditDashboardStats } from "../../types";
import AuditKpiCards from "./AuditKpiCards";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface AuditChartsProps {
  stats: AuditDashboardStats | null;
  loading?: boolean;
}

const BRAND = {
  primary: "#cd212a",
  secondary: "#2e436a",
  indigo: "#6366f1",
  emerald: "#10b981",
  slate: "#94a3b8",
  red: "#ef4444",
};

const BAR_ROW_HEIGHT = 40;
const BAR_ROW_HEIGHT_STACKED = 52;
const BAR_CHART_MIN = 220;
const BAR_CHART_MIN_STACKED = 320;
const BAR_CHART_MAX = 560;
const BAR_CHART_MAX_STACKED = 720;
const BAR_SCROLL_MAX = 520;
const BAR_SCROLL_MAX_STACKED = 640;
const MANY_TASKS_THRESHOLD = 5;

function ChartSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-secondary/10" />
        ))}
      </div>
      <div className="h-80 rounded-2xl bg-secondary/10" />
      <div className="h-96 rounded-2xl bg-secondary/10" />
    </div>
  );
}

export default function AuditCharts({ stats, loading }: AuditChartsProps) {
  const chartData = useMemo(() => {
    if (!stats) return null;

    const breakdown = stats.status_breakdown;
    const doughnutValues = [
      breakdown.active,
      breakdown.completed,
      breakdown.cancelled,
      breakdown.overdue,
    ];
    const doughnutHasData = doughnutValues.some((v) => v > 0);

    const taskBars = stats.task_bars ?? [];
    const barHasData = taskBars.length > 0;
    const manyTasks = taskBars.length > MANY_TASKS_THRESHOLD;

    const shortLabels = taskBars.map((t) => {
      const sameUserCount = taskBars.filter((x) => x.username === t.username).length;
      if (sameUserCount > 1) {
        const index =
          taskBars.filter((x) => x.username === t.username).findIndex((x) => x.id === t.id) + 1;
        return `${t.username} (${index})`;
      }
      return t.username;
    });

    const rowHeight = manyTasks ? BAR_ROW_HEIGHT_STACKED : BAR_ROW_HEIGHT;
    const barMin = manyTasks ? BAR_CHART_MIN_STACKED : BAR_CHART_MIN;
    const barMax = manyTasks ? BAR_CHART_MAX_STACKED : BAR_CHART_MAX;
    const scrollCap = manyTasks ? BAR_SCROLL_MAX_STACKED : BAR_SCROLL_MAX;

    const barHeight = Math.min(
      Math.max(taskBars.length * rowHeight + 88, barMin),
      barMax
    );
    const scrollMaxHeight = Math.min(barHeight + 32, scrollCap);

    return {
      doughnutHasData,
      barHasData,
      manyTasks,
      taskBars,
      shortLabels,
      barHeight,
      scrollMaxHeight,
      doughnut: {
        labels: ["Attive", "Completate", "Annullate", "In ritardo"],
        datasets: [
          {
            data: doughnutHasData ? doughnutValues : [1, 1, 1, 1],
            backgroundColor: [BRAND.indigo, BRAND.emerald, BRAND.slate, BRAND.red],
            borderColor: "#ffffff",
            borderWidth: 3,
            hoverOffset: 8,
          },
        ],
      },
      taskBar: {
        labels: shortLabels,
        datasets: [
          {
            label: "Completati",
            data: taskBars.map((t) => t.completed),
            backgroundColor: BRAND.emerald,
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: "Da fare",
            data: taskBars.map((t) => t.remaining),
            backgroundColor: "rgba(46, 67, 106, 0.14)",
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
    };
  }, [stats]);

  if (loading) return <ChartSkeleton />;
  if (!stats || !chartData) {
    return (
      <div className="rounded-2xl border border-dashed border-secondary/20 bg-white/80 p-12 text-center">
        <p className="text-lg font-bold text-secondary">Nessun dato per i grafici</p>
        <p className="mt-1 text-sm text-secondary/60">Assegna una task per vedere le statistiche</p>
      </div>
    );
  }

  const doughnutOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: chartData.doughnutHasData,
        callbacks: {
          label: (ctx) => {
            const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
      datalabels: {
        color: "#fff",
        font: { weight: "bold", size: chartData.manyTasks ? 13 : 11 },
        formatter: (value: number, ctx) => {
          if (!chartData.doughnutHasData) return "";
          const data = ctx.chart.data.datasets[0].data as number[];
          const total = data.reduce((a, b) => a + b, 0);
          if (!total || value === 0) return "";
          const pct = Math.round((value / total) * 100);
          return pct >= 8 ? `${pct}%` : "";
        },
      },
    },
  };

  const stackedBarOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: { padding: { left: 4, right: 16, top: 8, bottom: 8 } },
    datasets: {
      bar: {
        categoryPercentage: chartData.manyTasks ? 0.88 : 0.82,
        barPercentage: chartData.manyTasks ? 0.92 : 0.88,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: "rgba(46,67,106,0.06)" },
        ticks: { stepSize: 1, font: { size: chartData.manyTasks ? 12 : 11 } },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { size: chartData.manyTasks ? 13 : 12, weight: "bold" },
          color: BRAND.secondary,
          autoSkip: false,
          padding: 6,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 14,
          font: { size: chartData.manyTasks ? 12 : 11 },
        },
      },
      tooltip: {
        mode: "index",
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex ?? 0;
            const bar = chartData.taskBars[idx];
            return bar ? `${bar.username} · ${bar.completed}/${bar.target} lead` : "";
          },
        },
      },
      datalabels: { display: false },
    },
  };

  const doughnutCard = (
    <div className="rounded-2xl border border-white/80 bg-white p-5 shadow-lg shadow-secondary/5">
      <div className="mb-4">
        <h4 className="text-sm font-extrabold text-secondary">Distribuzione task</h4>
        <p className="text-xs text-secondary/50">Stato globale del team</p>
      </div>
      <div
        className={`relative mx-auto w-full ${
          chartData.manyTasks ? "h-80 max-w-[min(100%,22rem)] sm:max-w-md" : "h-52 max-w-[260px]"
        }`}
      >
        {!chartData.doughnutHasData ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-secondary/5 text-2xl text-secondary/30">
              ○
            </div>
            <p className="text-sm font-semibold text-secondary/60">Nessuna task ancora</p>
          </div>
        ) : (
          <Doughnut data={chartData.doughnut} options={doughnutOptions} />
        )}
      </div>
      <div
        className={`mt-4 grid gap-2 ${
          chartData.manyTasks
            ? "grid-cols-2 sm:grid-cols-4 sm:gap-3"
            : "grid-cols-2 sm:grid-cols-4"
        }`}
      >
        {chartData.doughnut.labels.map((label, idx) => (
          <div
            key={label}
            className={`flex items-center gap-2 text-secondary/80 ${
              chartData.manyTasks ? "text-sm" : "text-xs"
            }`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{
                backgroundColor: (chartData.doughnut.datasets[0].backgroundColor as string[])[idx],
              }}
            />
            <span className="font-medium">{label}</span>
            <span className="ml-auto font-bold tabular-nums text-secondary">
              {chartData.doughnutHasData
                ? (chartData.doughnut.datasets[0].data as number[])[idx]
                : 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const barCard = (
    <div className="flex min-h-0 flex-col rounded-2xl border border-white/80 bg-white p-5 shadow-lg shadow-secondary/5">
      <div className="mb-4 flex shrink-0 flex-wrap items-end justify-between gap-2">
        <div>
          <h4 className="text-sm font-extrabold text-secondary">Avanzamento per task attiva</h4>
          <p className="text-xs text-secondary/50">Lead completati vs obiettivo</p>
        </div>
        {chartData.barHasData && (
          <span className="rounded-lg bg-secondary/5 px-2.5 py-1 text-[11px] font-bold text-secondary/60">
            {chartData.taskBars.length} task attive
          </span>
        )}
      </div>
      {!chartData.barHasData ? (
        <div className="flex h-52 shrink-0 items-center justify-center rounded-xl border border-dashed border-secondary/15 bg-secondary/[0.02]">
          <p className="text-sm text-secondary/50">Nessuna task attiva da visualizzare</p>
        </div>
      ) : (
        <div
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-xl border border-secondary/8 bg-secondary/[0.015] p-2"
          style={{ maxHeight: chartData.manyTasks ? chartData.scrollMaxHeight : chartData.barHeight }}
        >
          <div
            className="w-full"
            style={{
              height: chartData.barHeight,
              minHeight: chartData.manyTasks ? BAR_CHART_MIN_STACKED : BAR_CHART_MIN,
            }}
          >
            <Bar data={chartData.taskBar} options={stackedBarOptions} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <AuditKpiCards stats={stats} />

      {chartData.manyTasks ? (
        <div className="flex flex-col gap-6">
          <div className="w-full">{doughnutCard}</div>
          <div className="w-full">{barCard}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
          {doughnutCard}
          {barCard}
        </div>
      )}
    </div>
  );
}
